/*
 * Article Logic Script for Shopify Blog
 * Manages dynamic placement of the Table of Contents (TOC) and product injection within blog articles.
 * On mobile (window.innerWidth <= 768), places the TOC before the first h2 tag within the article content container.
 * On desktop, places the TOC in the sidebar. Injects product grids before specific h2 tags based on article and product tags.
 * Handles resize events to ensure responsive TOC placement.
 */
document.addEventListener('DOMContentLoaded', () => {
  // --- Table of Contents Placement ---
  function buildAndPlaceTOC() {
    const tocList = document.getElementById('toc-list');
    const contentArea = document.querySelector('.m-article__content.rte');
    const tocContainer = document.getElementById('toc-sidebar');
    const desktopSidebar = document.querySelector('.m-blog-sidebar');
    const articleContentContainer = document.getElementById('article-content-container');

    if (!tocList || !contentArea || !tocContainer || !articleContentContainer) {
      console.error('TOC Script: Missing essential elements (tocList, contentArea, tocContainer, or articleContentContainer).');
      return;
    }

    tocList.innerHTML = '';
    const headings = articleContentContainer.querySelectorAll('h2');

    if (headings.length < 1) {
      tocContainer.style.display = 'none';
      return;
    }
    tocContainer.style.display = '';

    headings.forEach((heading, index) => {
      const headingText = heading.textContent.trim();
      if (!headingText) return;

      const headingId = heading.id || `toc-heading-${index}`;
      heading.id = headingId;

      const listItem = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${headingId}`;
      link.textContent = headingText;

      link.addEventListener('click', (e) => {
        e.preventDefault();
        document.querySelector(link.getAttribute('href')).scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });

      listItem.appendChild(link);
      tocList.appendChild(listItem);
    });

    const currentFirstHeading = articleContentContainer.querySelector('h2');

    if (window.innerWidth <= 768) {
      if (currentFirstHeading) {
        if (articleContentContainer.contains(currentFirstHeading)) {
          if (currentFirstHeading.previousElementSibling !== tocContainer) {
            if (!tocContainer.parentNode) {
              articleContentContainer.appendChild(tocContainer);
            }
            try {
              articleContentContainer.insertBefore(tocContainer, currentFirstHeading);
            } catch (e) {
              console.error('TOC Script: Failed to insert TOC before H2:', e);
              articleContentContainer.appendChild(tocContainer);
            }
          }
        } else {
          console.error('TOC Script: First H2 is not a child of articleContentContainer.');
          if (!articleContentContainer.contains(tocContainer)) {
            articleContentContainer.appendChild(tocContainer);
          }
        }
      } else {
        console.error('TOC Script: No H2 found in mobile view.');
        if (!articleContentContainer.contains(tocContainer)) {
          articleContentContainer.appendChild(tocContainer);
        }
      }
    } else {
      if (desktopSidebar && !desktopSidebar.contains(tocContainer)) {
        desktopSidebar.prepend(tocContainer);
      } else if (!desktopSidebar) {
        console.error('TOC Script: Desktop sidebar not found.');
        if (!contentArea.contains(tocContainer)) {
          contentArea.appendChild(tocContainer);
        }
      }
    }
  }

  // --- Product Injection ---
  const articleContentContainer = document.getElementById('article-content-container');
  const articleDataAndTemplatesContainer = document.getElementById('article-data-and-product-templates');

  if (!articleContentContainer || !articleDataAndTemplatesContainer) {
    console.error('Product Injector: Missing article-content-container or article-data-and-product-templates.');
    return;
  }

  let articleTags = [];
  try {
    const tagsScript = articleDataAndTemplatesContainer.querySelector('#article-tags-json');
    if (tagsScript) {
      articleTags = JSON.parse(tagsScript.textContent);
    } else {
      console.error('Product Injector: #article-tags-json not found.');
    }
  } catch (e) {
    console.error('Product Injector: Failed to parse article tags JSON:', e);
  }

  const allProductHtmlTemplates = articleDataAndTemplatesContainer.querySelectorAll('.product-card-html-template');
  let rawProducts = [];
  allProductHtmlTemplates.forEach(templateDiv => {
    const productId = templateDiv.dataset.productId;
    const productTags = templateDiv.dataset.productTags ? templateDiv.dataset.productTags.split(',').map(tag => tag.trim()) : [];
    const productHtml = templateDiv.innerHTML;

    if (productId && productHtml) {
      rawProducts.push({
        id: productId,
        tags: productTags,
        html: productHtml
      });
    }
  });

  const taggedProducts = [];
  const otherProducts = [];
  const usedIds = new Set();

  rawProducts.forEach(product => {
    if (!product.id || !product.html || !Array.isArray(product.tags)) {
      console.error('Product Injector: Skipping invalid product:', product);
      return;
    }

    const productTagsLower = product.tags.map(tag => tag.trim().toLowerCase());
    const articleTagsLower = articleTags.map(aTag => aTag.trim().toLowerCase());

    const hasMatchingTag = productTagsLower.some(tag => articleTagsLower.includes(tag));

    if (!usedIds.has(product.id)) {
      if (hasMatchingTag) {
        taggedProducts.push(product);
      } else {
        otherProducts.push(product);
      }
      usedIds.add(product.id);
    }
  });

  const finalProducts = [...taggedProducts, ...otherProducts];
  let productsToInject = [...finalProducts];

  const allH2s = articleContentContainer.querySelectorAll('h2');
  if (allH2s.length === 0) {
    document.getElementById('toc-sidebar').style.display = 'none';
    return;
  }

  const firstInjectionIndex = 2;
  const injectionInterval = 4;
  const productsPerGrid = 3;

  const gridPositions = [];
  if (allH2s.length > firstInjectionIndex) {
    gridPositions.push(firstInjectionIndex);
  }
  for (let i = firstInjectionIndex + injectionInterval; i < allH2s.length; i += injectionInterval) {
    gridPositions.push(i);
  }
  gridPositions.sort((a, b) => b - a);

  gridPositions.forEach(position => {
    const targetH2 = allH2s[position];
    if (!targetH2) {
      console.error('Product Injector: Target H2 not found at position', position);
      return;
    }

    const productsToInjectInCurrentGrid = productsToInject.slice(0, productsPerGrid);
    if (productsToInjectInCurrentGrid.length > 0) {
      productsToInject = productsToInject.slice(productsPerGrid);

      const gridContainer = document.createElement('div');
      gridContainer.className = 'm-product-grid-wrapper';
      gridContainer.style.display = 'grid';
      gridContainer.style.gridTemplateColumns = 'repeat(auto-fit, minmax(200px, 1fr))';
      gridContainer.style.gap = '20px';
      gridContainer.style.margin = '40px 0';

      productsToInjectInCurrentGrid.forEach(product => {
        if (product.html) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = product.html;
          if (tempDiv.firstElementChild) {
            gridContainer.appendChild(tempDiv.firstElementChild);
          }
        }
      });

      if (gridContainer.children.length > 0) {
        targetH2.insertAdjacentElement('beforebegin', gridContainer);
      }
    }
  });

  // --- Initialize TOC and Resize Listener ---
  buildAndPlaceTOC();

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      buildAndPlaceTOC();
    }, 250);
  });
});